import type { Coding } from "@medplum/fhirtypes";
import { RXNORM_SYSTEM } from "@repo/fhir-systems";
import { useEffect, useMemo, useState } from "react";
import { type RxDrugOption, useRxNormSearch } from "@/shared/hooks/use-rxnorm-search";
import { routeFromForm } from "./medication-sentence";

const RXNAV = "https://rxnav.nlm.nih.gov/REST";

/**
 * Plain products first: a single ingredient before a combination, and the oral
 * form before the rest. RxTerms ranks "vitamin D" as alendronate/cholecalciferol
 * first, and taking the top match would record an osteoporosis drug.
 */
function rank(option: RxDrugOption): number {
  return (option.name.includes("/") ? 2 : 0) + (routeFromForm(option.name) === "oral" ? 0 : 1);
}

export function rankDrugs(options: readonly RxDrugOption[]): RxDrugOption[] {
  return options
    .map((option, index) => ({ option, index }))
    .sort((a, b) => rank(a.option) - rank(b.option) || a.index - b.index)
    .map(({ option }) => option);
}

/**
 * RxTerms matches prefixes only, so "elliquis" finds nothing. RxNav's spelling
 * suggestions turn it back into "eliquis" — asked only once the search has
 * come back empty, so a correct spelling never waits on it.
 */
function useSpellingSuggestions(query: string, empty: boolean): { names: string[]; pending: boolean } {
  const [result, setResult] = useState<{ query: string; names: string[] } | null>(null);
  useEffect(() => {
    if (!empty || query.trim().length < 4) return;
    const controller = new AbortController();
    fetch(`${RXNAV}/spellingsuggestions.json?name=${encodeURIComponent(query.trim())}`, { signal: controller.signal })
      .then((response) => response.json())
      .then((data: { suggestionGroup?: { suggestionList?: { suggestion?: string[] } } }) => {
        if (!controller.signal.aborted) {
          setResult({ query, names: (data.suggestionGroup?.suggestionList?.suggestion ?? []).slice(0, 3) });
        }
      })
      .catch(() => {
        // No suggestions rather than none yet: a pending lookup holds Enter.
        if (!controller.signal.aborted) setResult({ query, names: [] });
      });
    return () => controller.abort();
  }, [query, empty]);
  const asked = empty && query.trim().length >= 4;
  const answered = result?.query === query;
  return { names: asked && answered ? result.names : [], pending: asked && !answered };
}

export function useDrugSearch(query: string) {
  const { options, isLoading } = useRxNormSearch(query);
  const ranked = useMemo(() => rankDrugs(options), [options]);
  const spelling = useSpellingSuggestions(query, !isLoading && query.trim().length >= 2 && options.length === 0);
  return { options: ranked, suggestions: spelling.names, isLoading: isLoading || spelling.pending };
}

/**
 * A drug picked without a strength is still coded: at the level RxNav knows it
 * by name — the ingredient for "Spironolactone", the brand for "Eliquis". None
 * found leaves it uncoded, which is what "as typed" means anyway.
 */
export async function nameLevelCoding(name: string): Promise<Coding | undefined> {
  const base = name.replace(/\s*\([^)]*\)/g, "").trim();
  if (!base) return undefined;
  try {
    const response = await fetch(`${RXNAV}/rxcui.json?name=${encodeURIComponent(base)}&search=2`);
    const data = (await response.json()) as { idGroup?: { rxnormId?: string[] } };
    const code = data.idGroup?.rxnormId?.[0];
    return code ? { system: RXNORM_SYSTEM, code, display: base } : undefined;
  } catch {
    return undefined;
  }
}
